import { ThemeEvents } from '@theme/events';

const MAX_QUANTITY = 10;

/**
 * Formats cents with the shop's money format, e.g. "${{amount}}".
 * @param {number} cents
 * @param {string} format
 */
function formatMoney(cents, format) {
  /** @param {number} decimals @param {string} thousands @param {string} decimal */
  const toAmount = (decimals, thousands = ',', decimal = '.') => {
    const [whole, fraction] = (cents / 100).toFixed(decimals).split('.');
    const grouped = (whole || '0').replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  };

  return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_match, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return toAmount(0);
      case 'amount_with_comma_separator':
        return toAmount(2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return toAmount(0, '.');
      case 'amount_with_apostrophe_separator':
        return toAmount(2, "'");
      default:
        return toAmount(2);
    }
  });
}

/**
 * Optional add-ons on the product page. Keeps a quantity per add-on, keeps the
 * "Total" price in sync, and hands the selection to product-form.js on submit.
 */
class HbProductAddons extends HTMLElement {
  /** @type {Map<string, number>} */
  #quantities = new Map();
  #abortController = new AbortController();
  /** @type {MutationObserver | undefined} */
  #priceObserver;
  #priceWasChanged = false;

  connectedCallback() {
    const { signal } = this.#abortController;

    this.rail = /** @type {HTMLElement} */ (this.querySelector('.hb-addons__rail'));
    this.basePrice = Number(this.dataset.basePrice) || 0;

    this.addEventListener('click', this.#onClick, { signal });
    this.rail?.addEventListener('scroll', this.#updateArrows, { passive: true, signal });
    window.addEventListener('resize', this.#updateArrows, { passive: true, signal });

    this.closest('.shopify-section')?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    this.#observePrice();
    this.#updateArrows();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#priceObserver?.disconnect();
  }

  /** @returns {HTMLElement[]} */
  get cards() {
    return Array.from(this.querySelectorAll('[data-addon]'));
  }

  get addonsTotal() {
    let total = 0;
    for (const card of this.cards) {
      total += (this.#quantities.get(card.dataset.variantId || '') || 0) * (Number(card.dataset.price) || 0);
    }
    return total;
  }

  get selectedCount() {
    let count = 0;
    for (const quantity of this.#quantities.values()) count += quantity;
    return count;
  }

  /**
   * Line items for /cart/add.js. Each add-on records which gift it belongs to so the
   * florist knows where to pack it when a cart holds more than one gift.
   * @returns {Array<{id: number, quantity: number, properties: Record<string, string>}>}
   */
  getCartItems() {
    const propertyName = this.dataset.propertyName || 'Add-on for';
    const items = [];

    for (const [variantId, quantity] of this.#quantities) {
      if (quantity < 1) continue;
      items.push({
        id: Number(variantId),
        quantity,
        properties: { [propertyName]: this.dataset.giftTitle || '' },
      });
    }

    return items;
  }

  /** Clears every selection, e.g. after the add-ons are in the cart. */
  reset() {
    for (const card of this.cards) this.#setQuantity(card, 0);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const chip = target.closest('[data-filter]');
    if (chip instanceof HTMLElement) {
      this.#filter(chip);
      return;
    }

    const arrow = target.closest('[data-scroll]');
    if (arrow instanceof HTMLElement && this.rail) {
      this.rail.scrollBy({ left: Number(arrow.dataset.scroll) * this.rail.clientWidth * 0.8, behavior: 'smooth' });
      return;
    }

    if (target.closest('[data-clear]')) {
      this.reset();
      this.rail?.focus({ preventScroll: true });
      return;
    }

    const card = target.closest('[data-addon]');
    if (!(card instanceof HTMLElement)) return;
    const current = this.#quantities.get(card.dataset.variantId || '') || 0;

    const step = target.closest('[data-step]');
    if (step instanceof HTMLElement) {
      const next = current + Number(step.dataset.step);
      this.#setQuantity(card, next);
      if (next === 0) card.querySelector('.hb-addon__add')?.focus();
      return;
    }

    const add = target.closest('[data-add]');
    if (!add) return;

    // The photo toggles the add-on; the Add button only ever adds.
    if (add.classList.contains('hb-addon__media') && current > 0) {
      this.#setQuantity(card, 0);
      return;
    }

    if (current === 0) {
      this.#setQuantity(card, 1);
      if (add.classList.contains('hb-addon__add')) {
        /** @type {HTMLElement | null} */ (card.querySelector('[data-step="1"]'))?.focus();
      }
    }
  };

  /**
   * @param {HTMLElement} card
   * @param {number} quantity
   */
  #setQuantity(card, quantity) {
    const variantId = card.dataset.variantId || '';
    const next = Math.max(0, Math.min(MAX_QUANTITY, quantity));

    if (next === 0) this.#quantities.delete(variantId);
    else this.#quantities.set(variantId, next);

    const addButton = /** @type {HTMLElement | null} */ (card.querySelector('.hb-addon__add'));
    const stepper = /** @type {HTMLElement | null} */ (card.querySelector('[data-stepper]'));
    const qtyValue = card.querySelector('[data-qty-value]');
    const increase = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-step="1"]'));

    card.classList.toggle('is-selected', next > 0);
    if (addButton) addButton.hidden = next > 0;
    if (stepper) stepper.hidden = next === 0;
    if (qtyValue) qtyValue.textContent = String(next);
    if (increase) increase.disabled = next >= MAX_QUANTITY;

    this.#render();
  }

  #render() {
    const count = this.selectedCount;
    const total = this.addonsTotal;
    const format = this.dataset.moneyFormat || '';

    const summary = /** @type {HTMLElement | null} */ (this.querySelector('[data-summary]'));
    const summaryText = this.querySelector('[data-summary-text]');
    if (summary) summary.hidden = count === 0;
    if (summaryText) {
      const template = (count === 1 ? this.dataset.summaryOne : this.dataset.summaryOther) || '';
      summaryText.textContent = count
        ? template.replace('[count]', String(count)).replace('[amount]', formatMoney(total, format))
        : '';
    }

    const alcoholNotice = /** @type {HTMLElement | null} */ (this.querySelector('[data-alcohol-notice]'));
    if (alcoholNotice) {
      alcoholNotice.hidden = !this.cards.some(
        (card) => card.hasAttribute('data-alcohol') && this.#quantities.has(card.dataset.variantId || '')
      );
    }

    this.#applyTotalPrice();
  }

  /** @param {HTMLElement} chip */
  #filter(chip) {
    const filter = chip.dataset.filter || 'all';

    for (const button of this.querySelectorAll('[data-filter]')) {
      button.setAttribute('aria-pressed', String(button === chip));
    }
    for (const card of this.cards) {
      card.hidden = filter !== 'all' && card.dataset.category !== filter;
    }

    this.rail?.scrollTo({ left: 0 });
    this.#updateArrows();
  }

  #updateArrows = () => {
    const rail = this.rail;
    if (!rail) return;

    const maxScroll = rail.scrollWidth - rail.clientWidth;
    const prev = /** @type {HTMLElement | null} */ (this.querySelector('[data-scroll="-1"]'));
    const next = /** @type {HTMLElement | null} */ (this.querySelector('[data-scroll="1"]'));

    if (prev) prev.hidden = rail.scrollLeft <= 4;
    if (next) next.hidden = rail.scrollLeft >= maxScroll - 4;
  };

  /** The price element(s) for this gift in the product details, i.e. the "Total" row. */
  get #priceTargets() {
    const section = this.closest('.shopify-section');
    if (!section) return [];

    return Array.from(section.querySelectorAll(`product-price[data-product-id="${this.dataset.productId}"]`))
      .map((price) => price.querySelector('[ref="priceContainer"] .price'))
      .filter((price) => price instanceof HTMLElement);
  }

  #applyTotalPrice() {
    const total = this.addonsTotal;
    if (total === 0 && !this.#priceWasChanged) return;

    const text = formatMoney(this.basePrice + total, this.dataset.moneyFormat || '');
    for (const price of this.#priceTargets) {
      if (price.textContent?.trim() !== text) price.textContent = text;
    }
    this.#priceWasChanged = total > 0;
  }

  /** Re-applies the combined total when the theme re-renders the price (e.g. on variant change). */
  #observePrice() {
    const section = this.closest('.shopify-section');
    const priceBlocks = section?.querySelectorAll(`product-price[data-product-id="${this.dataset.productId}"]`);
    if (!priceBlocks?.length) return;

    this.#priceObserver = new MutationObserver(() => {
      if (this.addonsTotal > 0) this.#applyTotalPrice();
    });
    for (const block of priceBlocks) {
      this.#priceObserver.observe(block, { childList: true, subtree: true, characterData: true });
    }
  }

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    const { resource, data } = /** @type {any} */ (event).detail || {};
    if (data?.productId && String(data.productId) !== this.dataset.productId) return;
    if (resource && typeof resource.price === 'number') this.basePrice = resource.price;

    requestAnimationFrame(() => this.#applyTotalPrice());
  };
}

if (!customElements.get('hb-product-addons')) {
  customElements.define('hb-product-addons', HbProductAddons);
}
